import { forwardRef, ReactNode } from "react";
import { Route } from "react-router-dom";
import {
  ContractUrls,
  useContractUrls,
} from "../contract/routing/ContractUrls";
import { PageContent } from "./Page";
import styled from "styled-components";
import Card from "./Card";
import { onDesktop } from "../shared/layout/Breakpoints";
import { AppHistoryContext } from "../contract/routing/AppHistoryProvider";
import { LocationDescriptor } from "history";
import { NavigationDirection } from "../contract/routing/ContractFormRoutes";
import {
  ClickBackLink,
  LinkBackLink,
} from "@teamscheme/page-navigation-footer";

export interface PageCardBackButtonProps {
  back?: LocationDescriptor;
  backUrl?: (urls: ContractUrls) => LocationDescriptor;
  historyBack?: boolean;
  onClickBack?: () => void;
}

export function PageCardBackButton({
  back,
  backUrl,
  historyBack,
  onClickBack,
}: PageCardBackButtonProps) {
  const urls = useContractUrls();

  if (backUrl) {
    back = backUrl(urls);
  }

  if (historyBack) {
    return (
      <AppHistoryContext.Consumer>
        {(context) =>
          context && context.appHistory.length > 1 ? (
            <Route>
              {({ history }) => (
                <ClickBackLink
                  onClick={() => {
                    history.goBack();
                  }}
                />
              )}
            </Route>
          ) : (
            <LinkBackLink to="/" />
          )
        }
      </AppHistoryContext.Consumer>
    );
  }
  if (back) {
    return (
      <LinkBackLink
        to={
          typeof back === "string"
            ? back
            : {
                ...back,
                state: { direction: NavigationDirection.Back },
              }
        }
        onClick={onClickBack}
      >
        Tilbake
      </LinkBackLink>
    );
  }

  if (onClickBack) {
    return <ClickBackLink onClick={onClickBack} />;
  }

  return null;
}

export interface PageCardProps extends PageCardBackButtonProps {
  children: ReactNode;
  className?: string;
  menu?: ReactNode;
}

export const PageCardContent = styled.div``;

const PageCardTop = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.space16};
`;

const PageCardInner = forwardRef<HTMLDivElement | null, PageCardProps>(
  function (
    { children, back, backUrl, onClickBack, historyBack, className, menu },
    ref
  ) {
    return (
      <PageContent>
        <Card ref={ref} className={className}>
          <PageCardTop>
            <div>
              <PageCardBackButton
                back={back}
                backUrl={backUrl}
                onClickBack={onClickBack}
                historyBack={historyBack}
              />
            </div>
            <div>
              {menu ? <div className="PageCard-menu">{menu}</div> : null}
            </div>
          </PageCardTop>
          <PageCardContent>{children}</PageCardContent>
        </Card>
      </PageContent>
    );
  }
);

const PageCard = styled(PageCardInner)`
  ${onDesktop`
    ${Card} {
      margin-bottom: 2em;
    }
  `}
`;

export default PageCard;
